import { useParams, useLocation } from 'react-router-dom'
import nth from 'lodash/nth'

import RequestForQuote from '@/gf/modules/RequestForQuote'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import { useRfqCreateQuoteQuery } from '@/buyers/_gen/gql'
import useWindowWidth from '@/gf/hooks/useWindowWidth'

import Page from '@/gf/components/Page'
import Frame from '@/buyers/components/Frame'
import Form from './CreateQuote/Form'

const CreateQuote = () => {
  const {
    organization: { id: orgId },
  } = useSession()

  const location = useLocation()
  const { rfqId, vendorId } = useParams() as { rfqId: string; vendorId: string }
  const windowWidth = useWindowWidth()

  const queryResult = useRfqCreateQuoteQuery({
    variables: { orgId, filter: JSON.stringify(['id_eq', rfqId]) },
    client: useGqlClient(),
  })

  const rfq = queryResult.data && (nth(queryResult.data.rfqs, 0) || null)
  const vendorLink = rfq?.vendorLinks.find((vl) => vl.vendor.id === vendorId)

  if (!rfq || !vendorLink) return null

  if (rfq.closed) {
    return (
      <Frame breadcrumbs={{ copy: 'Back to Dashboard', crumbs: [{ name: 'RFQs', href: '/rfqs' }] }}>
        <Page title="Enter Quote" previousPage={`/rfqs/${rfqId}/request`}>
          <p className="text-gray-600">This RFQ is closed and can no longer accept quotes.</p>
        </Page>
      </Frame>
    )
  }

  const windowIsMd = windowWidth > 768

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'RFQs', href: '/rfqs' },
      { name: `RFQ ${RequestForQuote.shortenId(rfqId)}`, href: `/rfqs/${rfqId}` },
      { name: 'Enter Quote', href: location.pathname },
    ],
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Enter Quote" previousPage={`/rfqs/${rfqId}/request`}>
        {/* The form owns the two-column layout: the upload card, the totals and the
            submit button all live in the right rail, so they have to sit inside the
            same <form> element as the parts list (GEA-6418). */}
        <Form
          // Remount when the quote's subject changes. This route keeps the same element
          // across param changes, and the form seeds its parts once on mount and resumes
          // an in-flight upload from a per-RFQ+vendor key — both of which need the fresh
          // mount to be correct if a buyer moves between two Enter Quote pages.
          key={`${rfq.id}:${vendorLink.vendor.id}`}
          rfq={rfq}
          vendor={vendorLink.vendor}
          vendorLink={vendorLink}
          windowIsMd={windowIsMd}
        />
      </Page>
    </Frame>
  )
}

export default CreateQuote
